import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type {
	ContactDataDataRequest,
	ContactSource,
	ContactVerificationDataDataRequest,
	EmailFormData,
	PhoneFormData,
} from '@gov-nx/core/hooks';
import {
	CommunicationType,
	isROSSource,
	useAuthorizationConsentYupSchema,
} from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { PageCode } from '@gov-nx/module/page';
import {
	phoneShapeToString,
	useEmailShape,
	usePhoneShapeWithCountryCodes,
} from '@gov-nx/utils/common';

export interface EmailFormInstanceProps {
	isNeedForVerification: boolean;
	isLowAuthorization: boolean;
}

export function EmailFormInstance({
	isNeedForVerification,
	isLowAuthorization,
}: EmailFormInstanceProps): FormDefinition<EmailFormData> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const code = PageCode['nastaveni'];
	const tsn = getLocalizeCurried(code);
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization && isNeedForVerification,
	});
	const { getEmailShape } = useEmailShape();

	const formSchema = yup
		.object({
			email: getEmailShape({
				isRequired: true,
			}),
			code: yup
				.string()
				.optional()
				.when([], {
					is: () => isNeedForVerification,
					then: (schema) => schema.required(tsn('formular.validace.kod')),
				}),
			__souhlas: consentSchema,
		})
		.required();

	const formMethods = useGovForm<EmailFormData>({
		formSchema,
		defaultValues: {
			email: undefined,
			code: undefined,
			__souhlas: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export interface PhoneFormInstanceProps {
	isNeedForVerification: boolean;
	isLowAuthorization: boolean;
}

export function PhoneFormInstance({
	isNeedForVerification,
	isLowAuthorization,
}: PhoneFormInstanceProps): FormDefinition<PhoneFormData> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const code = PageCode['nastaveni'];

	const tsn = getLocalizeCurried(code);
	const { getPhoneShape, getDefaultValue } = usePhoneShapeWithCountryCodes();
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: isLowAuthorization && isNeedForVerification,
	});

	const formSchema = yup
		.object({
			telefon: getPhoneShape({ isRequired: true }),
			code: yup
				.string()
				.optional()
				.when([], {
					is: () => isNeedForVerification,
					then: (schema) => schema.required(tsn('formular.validace.kod')),
				}),
			__souhlas: consentSchema,
		})
		.required();

	const formMethods = useGovForm<PhoneFormData>({
		formSchema,
		defaultValues: {
			telefon: getDefaultValue(),
			code: undefined,
			__souhlas: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareUpdateEmailFormSubmitData = (
	contactSource: ContactSource,
	{ email }: Pick<EmailFormData, 'email'>
): ContactDataDataRequest => ({
	body: {
		zdroj: contactSource.source,
		ico: isROSSource(contactSource) ? contactSource.ico : undefined,
		hodnota: email,
		typ: CommunicationType.EMAIL,
	},
});

export const prepareVerifyEmailFormSubmitData = (
	contactSource: ContactSource,
	formData: Pick<EmailFormData, 'email' | 'code'>
): ContactVerificationDataDataRequest => ({
	body: {
		zdroj: contactSource.source,
		ico: isROSSource(contactSource) ? contactSource.ico : undefined,
		hodnota: formData.email,
		typ: CommunicationType.EMAIL,
		kod: formData.code ?? '',
	},
});

export const prepareUpdatePhoneFormSubmitData = (
	contactSource: ContactSource,
	formData: Pick<PhoneFormData, 'telefon'>
): ContactDataDataRequest => ({
	body: {
		zdroj: contactSource.source,
		ico: isROSSource(contactSource) ? contactSource.ico : undefined,
		hodnota: phoneShapeToString(formData.telefon),
		typ: CommunicationType.TELEFON,
	},
});

export const prepareVerifyPhoneFormSubmitData = (
	contactSource: ContactSource,
	formData: PhoneFormData
): ContactVerificationDataDataRequest => ({
	body: {
		zdroj: contactSource.source,
		ico: isROSSource(contactSource) ? contactSource.ico : undefined,
		hodnota: phoneShapeToString(formData.telefon),
		typ: CommunicationType.TELEFON,
		kod: formData.code ?? '',
	},
});

export const prepareDeleteFormSubmitData = (
	contactSource: ContactSource,
	typ: CommunicationType
): ContactDataDataRequest => ({
	body: {
		zdroj: contactSource.source,
		ico: isROSSource(contactSource) ? contactSource.ico : undefined,
		hodnota: '',
		typ,
	},
});
